// Shared number formatters for the Forecast Explorer views. Extracted from the
// per-component duplicates so SeriesRankTable / SeriesDetail / BudgetRankTable /
// BudgetDetail / DriverWaterfall share one implementation.

// fmtEur formats a whole-euro value as "€12,345".
export function fmtEur(v) {
  if (v == null) return "—";
  return `€${Math.round(v).toLocaleString("en-US")}`;
}

// fmtEurSigned formats a signed euro value, e.g. "+€1,200" or "-€340".
export function fmtEurSigned(v) {
  if (v == null) return "—";
  const abs = `€${Math.abs(Math.round(v)).toLocaleString("en-US")}`;
  return v >= 0 ? `+${abs}` : `-${abs}`;
}

// fmtPct formats a growth fraction (e.g. 0.15) as "+15.0%" or "-15.0%".
export function fmtPct(v) {
  if (v == null) return "—";
  const pct = (v * 100).toFixed(1);
  return v >= 0 ? `+${pct}%` : `${pct}%`;
}

// fmtSmape formats a sMAPE value (range [0,2]) as a percentage string.
export function fmtSmape(smape) {
  if (smape == null) return "not yet rated";
  return (smape * 100).toFixed(1) + "%";
}

// humanizeUnits rewrites the verbose IEC unit names cloud providers emit (GCP
// sends "gibibyte hour", "byte-seconds", …) into the compact forms people
// recognise. Order matters: compound units ("gibibyte hour") must match before
// the bare unit ("gibibyte"). Case-insensitive; only rewrites the unit tokens,
// leaving the rest of the label untouched.
const UNIT_REPLACEMENTS = [
  [/\bgibibyte[\s-]?hours?\b/gi, "GB-hour"],
  [/\bgibibyte[\s-]?months?\b/gi, "GB-month"],
  [/\bgibibyte[\s-]?days?\b/gi, "GB-day"],
  [/\bgibibytes?\b/gi, "GB"],
  [/\bmebibyte[\s-]?hours?\b/gi, "MB-hour"],
  [/\bmebibytes?\b/gi, "MB"],
  [/\btebibyte[\s-]?hours?\b/gi, "TB-hour"],
  [/\btebibytes?\b/gi, "TB"],
  [/\bkibibytes?\b/gi, "KB"],
  [/\bpebibytes?\b/gi, "PB"],
  [/\bbyte[\s-]?seconds?\b/gi, "byte-sec"],
];

export function humanizeUnits(label) {
  if (label == null) return label;
  let s = String(label);
  for (const [re, to] of UNIT_REPLACEMENTS) s = s.replace(re, to);
  return s;
}

// describeUnit explains a raw cloud usage unit ("gibibyte hour", "hour", "count")
// in plain terms. The category mirrors the backend's own usage-unit classifier
// (allocation_driver_gcp_usage.sql): byte/gibibyte → Storage, seconds → Compute,
// request/count → Requests. Returns { unit, category, hint } where `unit` is the
// compact form, `category` a one-word bucket (or null when unrecognised), and
// `hint` a sentence for a tooltip. Order matters: storage is tested before
// "second" so "byte-seconds" reads as storage-over-time, not compute.
export function describeUnit(raw) {
  const u = String(raw ?? "").toLowerCase().trim();
  const unit = humanizeUnits(raw);
  if (!u) return { unit, category: null, hint: null };

  if (/request|count|operation/.test(u)) {
    return { unit, category: "Requests", hint: "Number of API requests or operations performed." };
  }
  if (/byte|gibibyte|mebibyte|tebibyte|kibibyte|pebibyte/.test(u)) {
    const overTime = /hour|second|minute|month|day/.test(u);
    return {
      unit,
      category: "Storage",
      hint: overTime
        ? "Data held over time (e.g. GB kept per hour) — a storage cost."
        : "Amount of data stored — a storage cost.",
    };
  }
  if (/second/.test(u)) {
    return { unit, category: "Compute", hint: "Processing time in CPU-seconds — a compute cost." };
  }
  if (/minute/.test(u)) {
    return { unit, category: "Compute", hint: "Usage time billed per minute (e.g. compute / instance time)." };
  }
  if (/hour/.test(u)) {
    return { unit, category: "Compute", hint: "Usage time billed per hour (e.g. compute / instance time)." };
  }
  if (/\b(day|week|month|year)\b/.test(u)) {
    return { unit, category: "Usage", hint: "A time-metered quantity billed per period (e.g. a monthly amount)." };
  }
  return { unit, category: null, hint: null };
}

// unitDisplay turns a raw unit into a single readable label plus a tooltip hint,
// e.g. "Storage (GB-hours)" / "Compute (hours)". Falls back to the compact unit
// alone when the unit isn't recognised.
export function unitDisplay(raw) {
  const d = describeUnit(raw);
  return { text: d.category ? `${d.category} · ${d.unit}` : d.unit, hint: d.hint };
}

// formatSeriesName makes a "<service>||<unit>" series name readable: it peels the
// trailing unit segment (separated by one or more pipes — the backend uses "||"),
// prettifies it, and surfaces the unit's meaning as a hint. Only reformats when
// that trailing segment is a recognised unit, so ordinary names (and names that
// merely contain a pipe) pass through unchanged via humanizeUnits.
export function formatSeriesName(name) {
  const raw = String(name ?? "");
  // Allocation sources are keyed "project:<id>" — give them a readable label.
  // A true project name needs a backend id→name lookup; until then, shorten the id.
  const proj = raw.match(/^project:(.+)$/);
  if (proj) {
    const id = proj[1];
    if (/^unknown/i.test(id)) {
      return { label: "Unknown project", hint: "Spend not attributed to a specific project." };
    }
    const short = id.length > 10 ? `${id.slice(0, 8)}…` : id;
    return { label: `Project ${short}`, hint: `Project ID: ${id}` };
  }
  const m = raw.match(/^(.*?)\|+([^|]+)$/);
  if (m) {
    const d = describeUnit(m[2]);
    if (d.category) {
      return { label: `${humanizeUnits(m[1].trim())} · ${d.category} (${d.unit})`, hint: d.hint };
    }
  }
  return { label: humanizeUnits(raw), hint: null };
}
